import { FixedLocationTypesQuery, useFixedLocationTypesQuery } from "@graphql/api";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { useEffect, useState } from "react";
import { PlaceParams } from "./Place";

export default function useFixedLocationsPlaceList(
  serviceAreas?: number | number[],
  hidePuds?: boolean,
) {
  const [placeList, setPlaceList] = useState<PlaceParams[]>([]);
  const { data: contentData, loading: contentLoading } = useContent([
    "beLocationDialogCommonCatsHeader",
    "beLocationDialogTopLocHeader",
  ]);
  const { loading: cfgLoading, data: cfgData } = useConfig(["locationMode"]);

  const { data, loading } = useFixedLocationTypesQuery({
    variables: {
      hidePuds,
      pudId: Session.getBooking().pudPersonId
        ? parseInt(Session.getBooking().pudPersonId)
        : undefined,
      serviceAreaId: Array.isArray(serviceAreas) ? serviceAreas[0] : serviceAreas,
    },
  });

  useEffect(() => {
    async function updatePlaceList() {
      if (!data || !contentData || !cfgData) return;
      const fixedLocations = createPlaces(data.fixedLocationTypes || []);
      const placeList: PlaceParams[] = [];
      const puds = pudType(fixedLocations);

      // Locations
      const locationLength = onlyLocations(fixedLocations).length;
      if (locationLength && cfgData.config.locationMode == "all_locations") {
        fixedLocations.forEach((t) => {
          if (t.locations?.length) {
            placeList.push(header(t.title));
            placeList.push(...t.locations);
          }
        });
      } else if (locationLength) {
        if (puds?.locations?.length) {
          placeList.push(header(puds.title));
          placeList.push(...puds.locations);
        }

        placeList.push(header(contentData.content.beLocationDialogTopLocHeader));
        placeList.push(...onlyLocations(fixedLocations));
      } else if (puds?.locations?.length) {
        placeList.push(header(puds.title));
        placeList.push(...puds.locations);
      }

      // Location Types
      if (onlyLocationsTypes(fixedLocations).length) {
        placeList.push(header(contentData.content.beLocationDialogCommonCatsHeader));
        placeList.push(...onlyLocationsTypes(fixedLocations));
      }

      setPlaceList(placeList);
    }
    updatePlaceList();
  }, [cfgLoading, contentLoading, loading, data?.fixedLocationTypes]);

  // Currently only one service area supported.
  // Note this is at the end of the function instead of earlier because we
  // can't skip hooks.
  if (Array.isArray(serviceAreas) && serviceAreas.length > 1) return [];

  return placeList;
}

function createPlaces(types: FixedLocationTypesQuery["fixedLocationTypes"]) {
  return types.map((lt) => ({
    key: `FIXED.${lt.type}`,
    locations: lt.locations?.length
      ? lt.locations.map((l, index) => ({
          source: "fixedHudson",
          title: l.displayName,
          description: l.displayAddress,
          key: `${index}.${lt.type}.${l.code}`,
          type: l.type,
          typestring: l.typeString,
          servicearea: l.serviceAreas,
          location: l,
          partiallocation: lt.type == "PUD" ? l : undefined,
        }))
      : undefined,
    source: "fixed",
    title: lt.label,
    type: lt.type,
    typestring: lt.typeString,
  }));
}

const header = (name: string) => ({
  key: `FIXED.HEADER.${name}`,
  source: "fixedHeader",
  title: name,
  type: "X",
});

function onlyLocations(types: PlaceParams[]) {
  return types
    .filter((t) => t.locations?.length && t.type != "PUD")
    .map((t) => t.locations)
    .flat();
}

function pudType(types: PlaceParams[]) {
  const pudType = types.filter((t) => t.locations?.length && t.type == "PUD");
  return pudType.length ? pudType[0] : undefined;
}

const onlyLocationsTypes = (types: PlaceParams[]) => types.filter((t) => !t.locations?.length);
